import {
    IFetchTasksRequest,
    IFetchTasksResponse,
    IFetchUserTaskCountRequest,
    ITaskCountResponse,
    ITaskEmailResponse,
    ITaskResponse,
    type ICreateTaskRequest,
    IWorkspacesTaskCountResponse
} from '~/components/Tasks/types/tasks.types';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { pick } from 'lodash';

export class TasksAdapter extends BaseHttpAdapter {
    // TODO: type data
    async _fetchUserTasks(
        params: IFetchTasksRequest
    ): Promise<IFetchTasksResponse> {
        return this.apiService.apiRequest<IFetchTasksResponse>({
            method: 'get',
            url: `/api/v2/tasks_optimized`,
            params
        });
    }

    async fetchWorkspaceTasks(
        workspaceId: string,
        params: IFetchTasksRequest
    ): Promise<ITaskResponse[]> {
        return this.apiService.apiRequest<ITaskResponse[]>({
            method: 'get',
            url: `/api/v2/tasks/workspaces/${workspaceId}`,
            params
        });
    }

    async _fetchUserTaskCount(
        params: IFetchUserTaskCountRequest
    ): Promise<ITaskCountResponse> {
        return this.apiService.apiRequest<ITaskCountResponse>({
            method: 'get',
            url: `/api/v2/tasks/user/counts`,
            params
        });
    }

    async _fetchWorkspaceTaskCount(
        workspaceId: string,
        params
    ): Promise<ITaskCountResponse> {
        return this.apiService.apiRequest<ITaskCountResponse>({
            method: 'get',
            url: `/api/v2/tasks/workspaces/${workspaceId}/counts`,
            params
        });
    }

    async fetchWorkspacesTaskCount(
        workspaceIds: string[]
    ): Promise<IWorkspacesTaskCountResponse[]> {
        return this.apiService.apiRequest<IWorkspacesTaskCountResponse[]>({
            method: 'get',
            url: `/api/v2/tasks/workspaces_tasks/count`,
            params: {
                workspaces_ids: workspaceIds.join(',')
            }
        });
    }

    async createTask(data: ICreateTaskRequest): Promise<ITaskResponse> {
        return this.apiService.apiRequest<ITaskResponse>({
            method: 'post',
            url: `/api/v2/tasks`,
            data
        });
    }

    async updateTask(task: ITaskResponse): Promise<ITaskResponse> {
        // Pick only the fields that are needed for the update and ignore everything else
        // Use whitelist to avoid sending unnecessary data to the server, including any future
        // fields that might be added to the task object to ease the work on frontend side
        const taskData = {
            ...pick(
                task,
                'description',
                'status',
                'due_date',
                'due_time',
                'project_id',
                'workspace_id',
                'reminder',
                'task_group_id',
                'reminder'
            ),
            assignee_id: task.assignee?._id
        };
        return this.apiService.apiRequest<ITaskResponse>({
            method: 'put',
            url: `/api/v2/tasks/${task._id}`,
            data: taskData
        });
    }

    async deleteTask(taskId: string) {
        return this.apiService.apiRequest<ITaskResponse>({
            method: 'delete',
            url: `/api/v2/tasks/${taskId}`
        });
    }

    async getEmail(taskId) {
        return this.apiService.apiRequest<ITaskEmailResponse>({
            method: 'get',
            url: `/api/v2/tasks/${taskId}/email`
        });
    }

    async notifyAssignee(taskId: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/tasks/${taskId}/notify_assign`
        });
    }

    async importTasks(toWorkspaceId: string, fromWorkspaceId: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/tasks/workspaces/${toWorkspaceId}/import`,
            params: {
                workspace_id_from: fromWorkspaceId
            }
        });
    }
}
